/**
 * Calendar arithmetic used by the hosting-window and countdown logic.
 *
 * Everything here operates on absolute instants. The launch moment is stored
 * as UTC and rendered into the page as an ISO 8601 string, so a cached page
 * stays correct no matter how long CloudFront serves it.
 */

/**
 * Add whole months, clamping to the end of the target month.
 *
 * Naive month arithmetic overflows: in plain JS, Aug 31 + 6 months lands on
 * Mar 3 because February has no 31st. A hosting window that silently gains
 * days is a (small) revenue leak and an (annoying) off-by-one in the expiry
 * emails, so the day is clamped to the last valid day of the target month.
 */
export function addMonths(date: Date, months: number): Date {
  const day = date.getUTCDate()
  const result = new Date(date.getTime())

  // Move to the 1st before shifting the month, so the shift itself can never
  // overflow, then clamp the day back.
  result.setUTCDate(1)
  result.setUTCMonth(result.getUTCMonth() + months)

  const daysInTargetMonth = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
  ).getUTCDate()

  result.setUTCDate(Math.min(day, daysInTargetMonth))
  return result
}

/** Offset, in ms, between wall-clock time in `timeZone` and UTC at `instant`. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type)
    if (!part) throw new Error(`missing ${type} in formatted parts`)
    return Number(part.value)
  }

  const wallClockAsUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    // hour12:false yields 24 for midnight in some ICU versions.
    read('hour') % 24,
    read('minute'),
    read('second'),
  )

  return wallClockAsUtc - instant.getTime()
}

/**
 * Convert a calendar date (`YYYY-MM-DD`) to the UTC instant of local midnight
 * in `timeZone`.
 *
 * The founder picks a date, not a moment. Storing that as UTC midnight would
 * make "launching March 1" hit zero at 7pm on Feb 28 for someone in Toronto --
 * so the date is anchored to midnight in the founder's own zone, captured at
 * creation time.
 */
export function zonedMidnightToUtc(isoDate: string, timeZone: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match) throw new Error(`expected YYYY-MM-DD, got: ${isoDate}`)

  const [, year, month, day] = match as unknown as [string, string, string, string]
  const targetWallClock = Date.UTC(Number(year), Number(month) - 1, Number(day))

  // Solve instant = wallClock - offset(instant). One iteration is enough
  // except near a DST transition, where the offset used for the guess differs
  // from the offset at the answer; a second pass converges.
  let instant = targetWallClock
  for (let i = 0; i < 2; i += 1) {
    instant = targetWallClock - zoneOffsetMs(new Date(instant), timeZone)
  }
  return new Date(instant)
}

/** Whole days between two instants, rounded up -- the countdown's headline number. */
export function daysUntil(target: Date, from: Date): number {
  const ms = target.getTime() - from.getTime()
  if (ms <= 0) return 0
  return Math.ceil(ms / 86_400_000)
}

export interface CountdownParts {
  readonly days: number
  readonly hours: number
  readonly minutes: number
  readonly seconds: number
  /** True once the target instant has passed. */
  readonly elapsed: boolean
}

/**
 * Decompose a remaining duration the same way the on-page countdown does.
 *
 * Shared deliberately. The server pre-renders these values into the HTML so
 * there is no blank flash before JS runs, and the inline script recomputes
 * them on load -- if the two used different rounding, every visitor would see
 * the number visibly change on first paint. `daysUntil` rounds up, which is
 * right for prose ("launching in 83 days") and wrong here, because the hours
 * field already carries the remainder.
 */
export function countdownParts(target: Date, from: Date): CountdownParts {
  const ms = target.getTime() - from.getTime()
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, elapsed: true }
  const total = Math.floor(ms / 1000)
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor(total / 3_600) % 24,
    minutes: Math.floor(total / 60) % 60,
    seconds: total % 60,
    elapsed: false,
  }
}
